import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Box, Button, Chip, CircularProgress, IconButton, Skeleton, TextField, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faArrowUpRightFromSquare,
  faBriefcase,
  faBuilding,
  faChartBar,
  faCircleExclamation,
  faLink,
  faMagnifyingGlass,
} from '@fortawesome/free-solid-svg-icons';

import { TranslationKeys } from '../../../../translations';
import { useBreadcrumb } from '../../../../components/Breadcrumbs';
import { useConnectionInspectorQuery } from '../../../../hooks/api/admin';
import type { ConnectionInspectorResult } from '../../../../hooks/api/admin';
import format from '../../../../utils/formatter';

import {
  PageWrapperStyled,
  SearchBarStyled,
  ResultsGridStyled,
  ColumnPairStyled,
  InfoCardStyled,
  CardHeaderBandStyled,
  CardIconCircleStyled,
  CardBodyStyled,
  FieldListStyled,
  FieldRowStyled,
  FieldValueStyled,
  PlaceholderStateStyled,
  NotFoundStateStyled,
  SkeletonBlockStyled,
} from './ConnectionInspectorPage.styles';

// ─── Card accent colours ──────────────────────────────────────────────────────

const COLOR_CONNECTION = '#00A0FF';
const COLOR_EMPLOYER = '#40C484';
const COLOR_OWNER = '#7C4DFF';
const COLOR_STATS = '#F3AE43';

// ─── Card state ───────────────────────────────────────────────────────────────

type CardState = 'idle' | 'loading' | 'found' | 'not-found';

// ─── Generic Info Card ────────────────────────────────────────────────────────

type InfoCardProps = {
  icon: IconDefinition;
  color: string;
  titleId: string;
  placeholderTextId: string;
  state: CardState;
  children?: React.ReactNode;
};

const InfoCard = ({ icon, color, titleId, placeholderTextId, state, children }: InfoCardProps) => (
  <InfoCardStyled elevation={0}>
    <CardHeaderBandStyled bandcolor={color}>
      <CardIconCircleStyled bandcolor={color}>
        <FontAwesomeIcon icon={icon} />
      </CardIconCircleStyled>
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: '0.72rem',
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color,
          lineHeight: 1.2,
        }}
      >
        <FormattedMessage id={titleId} />
      </Typography>
    </CardHeaderBandStyled>

    <CardBodyStyled>
      {state === 'idle' && (
        <PlaceholderStateStyled>
          <FontAwesomeIcon icon={faMagnifyingGlass} style={{ fontSize: '1.6rem' }} />
          <span>
            <FormattedMessage id={placeholderTextId} />
          </span>
        </PlaceholderStateStyled>
      )}

      {state === 'loading' && (
        <SkeletonBlockStyled>
          {[0, 1, 2, 3].map(i => (
            <React.Fragment key={i}>
              <Skeleton width="40%" height={11} sx={{ mb: 0.25 }} />
              <Skeleton width="70%" height={18} sx={{ mb: 1.5 }} />
            </React.Fragment>
          ))}
        </SkeletonBlockStyled>
      )}

      {state === 'not-found' && (
        <NotFoundStateStyled>
          <FontAwesomeIcon icon={faCircleExclamation} style={{ fontSize: '1.6rem' }} />
          <span>
            <FormattedMessage id={TranslationKeys.tools_connectionInspector_notFound} />
          </span>
        </NotFoundStateStyled>
      )}

      {state === 'found' && <FieldListStyled>{children}</FieldListStyled>}
    </CardBodyStyled>
  </InfoCardStyled>
);

// ─── Field row ────────────────────────────────────────────────────────────────

type FieldRowProps = {
  labelId: string;
  value: React.ReactNode;
  color: string;
  navigateTo?: string;
};

const FieldRow = ({ labelId, value, color, navigateTo }: FieldRowProps) => {
  return (
    <FieldRowStyled>
      <Typography
        sx={{
          fontSize: '0.67rem',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          color: `${color}bb`,
        }}
      >
        <FormattedMessage id={labelId} />
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
        <FieldValueStyled variant="body2">{value}</FieldValueStyled>
        {navigateTo && (
          <IconButton
            size="small"
            onClick={() => window.open(navigateTo, '_blank', 'noopener,noreferrer')}
            sx={{ color, p: 0.5, opacity: 0.7, '&:hover': { opacity: 1 } }}
            aria-label="open in new tab"
          >
            <FontAwesomeIcon icon={faArrowUpRightFromSquare} style={{ fontSize: '0.8rem' }} />
          </IconButton>
        )}
      </Box>
    </FieldRowStyled>
  );
};

// ─── Status chip (for boolean fields) ────────────────────────────────────────

type StatusChipProps = {
  value: boolean;
  trueLabelId: string;
  falseLabelId: string;
  /** When true, value=true is visually "bad" (red), value=false is "good" (green) */
  invertColors?: boolean;
};

const StatusChip = ({ value, trueLabelId, falseLabelId, invertColors = false }: StatusChipProps) => {
  const intl = useIntl();
  const isGood = invertColors ? !value : value;
  return (
    <Chip
      size="small"
      label={value ? intl.formatMessage({ id: trueLabelId }) : intl.formatMessage({ id: falseLabelId })}
      sx={{
        fontWeight: 700,
        fontSize: '0.7rem',
        height: 20,
        px: 0.25,
        backgroundColor: isGood ? '#40E4A115' : '#DF707015',
        color: isGood ? '#1cab72' : '#c55',
        border: `1px solid ${isGood ? '#40E4A145' : '#DF707045'}`,
      }}
    />
  );
};

// ─── Card field sets ─────────────────────────────────────────────────────────

const ConnectionFields = ({ data }: { data: ConnectionInspectorResult }) => (
  <>
    <FieldRow
      color={COLOR_CONNECTION}
      labelId={TranslationKeys.tools_connectionInspector_connectionName}
      value={data.connectionName}
      navigateTo={`/employers/${data.employerId}/connections/${data.connectionId}/info`}
    />
    <FieldRow
      color={COLOR_CONNECTION}
      labelId={TranslationKeys.tools_connectionInspector_productName}
      value={data.productName}
    />
    <FieldRow
      color={COLOR_CONNECTION}
      labelId={TranslationKeys.tools_connectionInspector_salaryPackage}
      value={<FormattedMessage id={`packages.type.${data.salaryPackage}`} />}
    />
    <FieldRow
      color={COLOR_CONNECTION}
      labelId={TranslationKeys.tools_connectionInspector_createdAt}
      value={format.date(data.createdAt)}
    />
    <FieldRow
      color={COLOR_CONNECTION}
      labelId={TranslationKeys.tools_connectionInspector_latestRun}
      value={data.latestRun ? format.date(data.latestRun) : '—'}
    />
    <FieldRow
      color={COLOR_CONNECTION}
      labelId={TranslationKeys.tools_connectionInspector_deleted}
      value={
        <StatusChip
          value={data.deleted}
          trueLabelId={TranslationKeys.tools_connectionInspector_statusDeleted}
          falseLabelId={TranslationKeys.tools_connectionInspector_statusActive}
          invertColors
        />
      }
    />
  </>
);

const EmployerFields = ({ data }: { data: ConnectionInspectorResult }) => (
  <>
    <FieldRow
      color={COLOR_EMPLOYER}
      labelId={TranslationKeys.tools_connectionInspector_employerName}
      value={data.employerName}
      navigateTo={`/employers/${data.employerId}/info`}
    />
    <FieldRow
      color={COLOR_EMPLOYER}
      labelId={TranslationKeys.tools_connectionInspector_employerIsPensioenbijIntegration}
      value={
        <StatusChip
          value={data.employerIsPensioenbijIntegration}
          trueLabelId={TranslationKeys.tools_connectionInspector_yes}
          falseLabelId={TranslationKeys.tools_connectionInspector_no}
        />
      }
    />
  </>
);

const OwnerFields = ({ data }: { data: ConnectionInspectorResult }) => (
  <FieldRow
    color={COLOR_OWNER}
    labelId={TranslationKeys.tools_connectionInspector_ownerName}
    value={data.ownerName}
    navigateTo={`/owners/${data.ownerId}/info`}
  />
);

const StatsFields = ({ data }: { data: ConnectionInspectorResult }) => (
  <>
    <FieldRow
      color={COLOR_STATS}
      labelId={TranslationKeys.tools_connectionInspector_numberOfEmployees}
      value={data.numberOfEmployees}
    />
    <FieldRow
      color={COLOR_STATS}
      labelId={TranslationKeys.tools_connectionInspector_numberOfEvents}
      value={data.numberOfEvents}
    />
  </>
);

// ─── Page ─────────────────────────────────────────────────────────────────────

export const ConnectionInspectorPage = () => {
  useBreadcrumb(TranslationKeys.menu_adminPanel_connectionInspector);

  const intl = useIntl();
  const [inputValue, setInputValue] = React.useState('');
  const [submittedId, setSubmittedId] = React.useState('');

  const { data, isFetching, isFetched } = useConnectionInspectorQuery({
    variables: { connectionId: submittedId },
    options: { enabled: !!submittedId },
  });

  const handleInputChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  }, []);

  const handleSearch = React.useCallback(() => {
    setSubmittedId(inputValue.trim());
  }, [inputValue]);

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') handleSearch();
    },
    [handleSearch],
  );

  // Derive card states
  const connectionState: CardState = isFetching
    ? 'loading'
    : !submittedId
    ? 'idle'
    : isFetched && !data
    ? 'not-found'
    : data
    ? 'found'
    : 'idle';

  // Employer / Owner / Stats stay idle when connection is not found
  const dependentState: CardState = connectionState === 'not-found' ? 'idle' : connectionState;

  return (
    <PageWrapperStyled>
      {/* Compact search bar */}
      <SearchBarStyled elevation={0}>
        <FontAwesomeIcon icon={faMagnifyingGlass} style={{ color: '#bbb', fontSize: '0.85rem', flexShrink: 0 }} />
        <TextField
          size="small"
          placeholder={intl.formatMessage({ id: TranslationKeys.tools_connectionInspector_searchPlaceholder })}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          sx={{ width: 300 }}
          inputProps={{ 'aria-label': 'Connection ID' }}
        />
        <Button
          variant="contained"
          size="small"
          onClick={handleSearch}
          disabled={!inputValue.trim() || isFetching}
          startIcon={isFetching ? <CircularProgress size={13} color="inherit" /> : undefined}
          sx={{ flexShrink: 0 }}
        >
          <FormattedMessage id={TranslationKeys.tools_connectionInspector_searchButton} />
        </Button>
      </SearchBarStyled>

      {/* Always-visible result cards: Connection | Employer+Owner | Stats */}
      <ResultsGridStyled>
        {/* Column 1 — Connection */}
        <InfoCard
          icon={faLink}
          color={COLOR_CONNECTION}
          titleId={TranslationKeys.tools_connectionInspector_connectionBox}
          placeholderTextId={TranslationKeys.tools_connectionInspector_connectionData}
          state={connectionState}
        >
          {data && <ConnectionFields data={data} />}
        </InfoCard>

        {/* Column 2 — Employer stacked above Owner */}
        <ColumnPairStyled>
          <InfoCard
            icon={faBriefcase}
            color={COLOR_EMPLOYER}
            titleId={TranslationKeys.tools_connectionInspector_employerBox}
            placeholderTextId={TranslationKeys.tools_connectionInspector_employerData}
            state={dependentState}
          >
            {data && <EmployerFields data={data} />}
          </InfoCard>

          <InfoCard
            icon={faBuilding}
            color={COLOR_OWNER}
            titleId={TranslationKeys.tools_connectionInspector_ownerBox}
            placeholderTextId={TranslationKeys.tools_connectionInspector_ownerData}
            state={dependentState}
          >
            {data && <OwnerFields data={data} />}
          </InfoCard>
        </ColumnPairStyled>

        {/* Column 3 — Stats */}
        <InfoCard
          icon={faChartBar}
          color={COLOR_STATS}
          titleId={TranslationKeys.tools_connectionInspector_statsBox}
          placeholderTextId={TranslationKeys.tools_connectionInspector_statsData}
          state={dependentState}
        >
          {data && <StatsFields data={data} />}
        </InfoCard>
      </ResultsGridStyled>
    </PageWrapperStyled>
  );
};
